import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmployee } from "@/constants/userTypes";
import {
  Menu,
  Bell,
  Search,
  User,
  LogOut,
  Moon,
  Sun,
  Monitor,
  PanelLeftClose,
  PanelLeft,
  Ticket,
  CheckSquare,
  MessageSquare,
  FileText,
  Clock,
  CalendarDays,
  Info,
  Check,
  Loader2,
  Building2,
  Gift,
  AlertCircle,
  MessageCircle,
  FolderKanban,
  Plane,
  Palmtree,
  ShoppingCart,
  CreditCard,
  FileInput,
  Receipt,
  UserX,
  CheckCircle,
  XCircle,
  StickyNote,
  ClipboardCheck,
  X,
} from "lucide-react";
import ChatSidebar from "@/components/chat/ChatSidebar";
import { openDesktopInstallPrompt } from "@/components/desktop-app/DesktopAppForceDialog";
import { useAndroidAppLatest } from "@/components/desktop-app/AndroidInstallSection";
import { useDesktopAppStatus, useMobileAppStatus } from "@/hooks/useDesktopAppStatus";
import { isAndroid, isWindowsDesktop } from "@/lib/platform";
import { isOutdatedVersion } from "@/lib/version";
import { useToast } from "@/hooks/use-toast";
import OrganizationSidebar from "@/components/organization/OrganizationSidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUIStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useNotificationStore, getNotificationRoute } from "@/stores/notificationStore";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { useNativeApp } from "@/hooks/useNativeApp";
import { openChatPopup } from "@/lib/chatPopup";
import { getInitials } from "@/lib/utils";
import { notificationService } from "@/services/notificationService";
import { searchService } from "@/services/searchService";
import chatService from "@/services/chatService";
import stickyNoteService from "@/services/stickyNoteService";
import lotteryService from "@/services/lotteryService";
import { competencyService } from "@/services/competencyService";
import widgetService from "@/services/widgetService";
import api, { getProfileImageUrl } from "@/services/api";
import { requestNotificationPermission } from "@/hooks/useWebSocket";
import type { Notification } from "@/types/notification";
import type { SearchResultItem } from "@/types/search";
import type { MenuItem } from "@/types/admin";
import authService from "@/services/authService";

// 메뉴 트리에서 특정 path가 존재하는지 재귀 검색
function hasMenuPath(menus: MenuItem[] | undefined, path: string): boolean {
  if (!menus) return false;
  return menus.some(m => m.path === path || hasMenuPath(m.children, path));
}

// 알림 타입별 아이콘
const notificationIcons: Record<string, React.ElementType> = {
  ticket: Ticket,
  task: CheckSquare,
  chat: MessageSquare,
  approval: FileText,
  approval_approved: CheckCircle,
  approval_rejected: XCircle,
  approval_comment: MessageCircle,
  attendance: Clock,
  attendance_absent_alert: UserX,
  schedule: CalendarDays,
  system: Info,
  sticky_note: StickyNote,
  lottery: Gift,
  issue: AlertCircle,
  customer_feedback: MessageCircle,
  project: FolderKanban,
  business_trip: Plane,
  leave: Palmtree,
  corporate_card: CreditCard,
  document_request: FileInput,
  payslip: Receipt,
  purchase: ShoppingCart,
};

export default function Header() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sidebarCollapsed, toggleSidebar, setSidebarOpen, theme, setTheme } =
    useUIStore();
  const { user, logout, switchCompany } = useAuthStore();
  const {
    notifications: storeNotifications,
    unreadCount: storeUnreadCount,
    setNotifications,
    setUnreadCount,
    markAsRead: markAsReadInStore,
    markAllAsRead: markAllAsReadInStore,
  } = useNotificationStore();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);
  const { toast } = useToast();
  // 관리자가 메신저 설치를 강제한 사용자(force_install)는 Windows PC 브라우저에서 채팅을 메신저 앱으로 유도한다 (대표 지시 2026-10-23).
  // 메신저가 지금 실행 중(WebSocket)이면 앱에서 보라는 안내만, 아니면(닫혔든 지웠든) 설치 안내 모달 — 모달에 "이미 설치했다면
  // 시작 메뉴에서 실행" 문구가 있어 두 경우를 모두 덮는다. "최근 30분 사용" 판정은 쓰지 않는다: 앱을 지운 뒤 30분 동안
  // "실행하세요" 만 떠서 설치 화면에 갈 길이 없던 문제 (대표 지적 2026-10-23).
  // 강제 설정이 없거나 Mac·모바일 웹·앱 WebView 면 웹 채팅 그대로. 자동 팝업은 없다 — 아이콘을 눌렀을 때만.
  const desktopApp = useDesktopAppStatus();
  const isNativeWebView =
    typeof window !== "undefined" && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;
  const routeChatToDesktopApp = isWindowsDesktop() && !isNativeWebView && desktopApp.data?.force_install === true;
  const desktopRunning = routeChatToDesktopApp && desktopApp.data?.running_here === true;
  // 버전은 보지 않는다 — 구버전도 앱이 알아서 자동 업데이트하므로 (대표 2026-10-23, 업데이트 모달 폐기)
  const desktopInstallNeeded = routeChatToDesktopApp && !desktopRunning;
  // 강제 대상이고 "해당 운영체제에 맞는 앱" 이 있으면 아이콘을 숨긴다 (대표 지시 2026-10-23):
  // Windows = 이 PC 에서 최신 메신저 실행 중, Android 브라우저 = 모바일 앱 설치됨(최근 14일 사용, 계정 단위).
  // Android 는 앱이 없어도 웹 채팅을 막지 않는다(모바일 웹 채팅 허용 방침).
  const mobileApp = useMobileAppStatus();
  // Android 브라우저 강제 대상: 앱 있으면 아이콘 숨김, 없으면 아이콘 클릭 → APK 설치 모달 (강제 아니면 웹 채팅).
  // 휴대폰 앱은 자동 업데이트가 없으므로 설치 버전이 배포 버전(android-latest.json)보다 낮으면 아이콘을 다시 보여 업데이트 안내 (대표 2026-10-23)
  const routeChatToMobileApp = isAndroid() && !isNativeWebView && mobileApp.data?.force_install === true;
  const androidLatest = useAndroidAppLatest(routeChatToMobileApp && mobileApp.present);
  const mobileOutdated =
    routeChatToMobileApp && mobileApp.present && isOutdatedVersion(mobileApp.data?.version, androidLatest.data?.version);
  const hideChatIcon =
    (routeChatToDesktopApp && desktopRunning) || (routeChatToMobileApp && mobileApp.present && !mobileOutdated);
  const handleChatIconClick = () => {
    if (routeChatToMobileApp) {
      openDesktopInstallPrompt({
        platform: "android",
        update: mobileOutdated
          ? { currentVersion: mobileApp.data?.version ?? null, latestVersion: androidLatest.data?.version ?? null }
          : undefined,
      });
      return;
    }
    if (!routeChatToDesktopApp) {
      setChatSidebarOpen(true);
      return;
    }
    if (desktopRunning) {
      toast({
        title: "메신저 앱에서 확인하세요",
        description: "이 PC의 HubCore 메신저가 실행 중입니다. 작업 표시줄(트레이)의 메신저에서 대화를 확인하세요.",
      });
      return;
    }
    openDesktopInstallPrompt({ platform: "windows" });
  };
  const [orgSidebarOpen, setOrgSidebarOpen] = useState(false);
  const [showEvaluationBanner, setShowEvaluationBanner] = useState(true);
  const { isMobile, isTablet } = useMobileDetect();
  const { isWebView } = useNativeApp();
  const isInlineMode = isMobile || isTablet || isWebView;

  // 알림 목록 조회
  const { data: notificationData, isLoading: isLoadingNotifications } = useQuery({
    queryKey: ["notifications", "header"],
    queryFn: () => notificationService.getNotifications({ page: 1, page_size: 10 }),
    refetchInterval: 60000, // 1분마다 갱신
  });

  // 채팅방 목록 조회 (읽지 않은 메시지 수 계산용)
  const { data: chatRooms = [] } = useQuery({
    queryKey: ["chat-rooms"],
    queryFn: () => chatService.getRooms(),
    refetchInterval: 30000, // 30초마다 갱신
  });

  // 사용자 접근 가능 메뉴 (Sidebar 캐시 재사용)
  const { data: userMenus } = useQuery({
    queryKey: ["user-menus"],
    queryFn: authService.getMyMenus,
    staleTime: 1000 * 60,
  });

  // 사용자 접근 가능 위젯 목록 조회
  const { data: myWidgets = [] } = useQuery<string[]>({
    queryKey: ["my-widgets"],
    queryFn: widgetService.getMyWidgets,
    enabled: !!(user && isEmployee(user.user_type)),
    staleTime: 1000 * 60,
  });

  // 포스트잇 미확인 개수 조회
  const { data: stickyNoteUnreadData } = useQuery({
    queryKey: ["sticky-notes-unread-count"],
    queryFn: () => stickyNoteService.getUnreadCount(),
    enabled: !!(user && isEmployee(user.user_type) && myWidgets.includes("sticky-notes")),
    refetchInterval: 60000, // 1분마다 갱신
  });

  // 미개봉 복권 수 조회
  const { data: lotteryPendingData } = useQuery({
    queryKey: ["lottery-pending-count"],
    queryFn: () => lotteryService.getMyPendingCount(),
    enabled: !!(user && isEmployee(user.user_type) && hasMenuPath(userMenus, "/lottery")),
    refetchInterval: 60000,
  });

  // 조직도 접근 가능 여부 + 활성 직원 존재 여부 확인
  const hasOrgAccess = !!(user && isEmployee(user.user_type) && myWidgets.includes("organization"));
  const { data: orgChartData } = useQuery({
    queryKey: ["organization-chart"],
    queryFn: async () => {
      const response = await api.get("/organization/chart");
      return response.data;
    },
    enabled: hasOrgAccess,
    staleTime: 1000 * 60 * 5, // 5분 캐시 (OrganizationSidebar와 공유)
  });

  // 사용자가 소속된 기업 목록 조회
  const { data: myCompaniesData } = useQuery({
    queryKey: ["my-companies"],
    queryFn: authService.getMyCompanies,
    enabled: !!user, // 로그인된 경우에만 실행
    staleTime: 1000 * 60 * 5, // 5분간 캐시
    retry: 1,
  });

  // 역량 평가 미완료 개수 조회 (배너용)
  const { data: competencyPendingData } = useQuery({
    queryKey: ["competency-pending-count"],
    queryFn: () => competencyService.getMyPendingCount(),
    enabled: !!(user && isEmployee(user.user_type)),
    refetchInterval: 60000, // 1분마다 갱신
  });


  // 총 읽지 않은 채팅 메시지 수
  const totalChatUnreadCount = chatRooms.reduce((sum, room) => sum + (room.unread_count ?? 0), 0);

  // 역량 평가 업무 개수
  const competencyEvaluationCount = competencyPendingData?.count || 0;

  // 알림 읽음 처리
  const markAsReadMutation = useMutation({
    mutationFn: notificationService.markAsRead,
    onSuccess: (_, notificationId) => {
      markAsReadInStore(notificationId);
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // 모든 알림 읽음 처리
  const markAllAsReadMutation = useMutation({
    mutationFn: notificationService.markAllAsRead,
    onSuccess: () => {
      markAllAsReadInStore();
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // 알림 데이터 동기화
  useEffect(() => {
    if (notificationData) {
      setNotifications(notificationData.items);
      setUnreadCount(notificationData.unread_count);
    }
  }, [notificationData, setNotifications, setUnreadCount]);

  // 최근 검색어 로드
  useEffect(() => {
    const recent = searchService.getRecentSearches();
    setRecentSearches(recent.map((r) => r.query));
  }, [searchOpen]);

  // 검색 수행
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      console.log("🔍 검색 시작:", query);
      const response = await searchService.quickSearch(query);
      console.log("✅ 검색 응답:", response);
      const allResults = response.results.flatMap((group) => group.items);
      console.log("📊 검색 결과:", allResults.length, "개");
      setSearchResults(allResults);
    } catch (error) {
      console.error("❌ 검색 실패:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // 디바운스된 검색
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // 검색 결과 선택
  const handleSelectResult = (result: SearchResultItem) => {
    searchService.saveRecentSearch(searchQuery);
    setSearchOpen(false);
    setSearchQuery("");
    navigate(result.url);
  };

  // 알림 클릭
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // 채팅 알림 처리
    if (notification.reference_type === "chat_room" && notification.reference_id) {
      if (isInlineMode) {
        // 모바일/WebView: 같은 창에서 채팅방으로 이동
        navigate(`/chat/popup/${notification.reference_id}`);
      } else {
        // 데스크톱: 팝업으로 열기
        openChatPopup(notification.reference_id);
      }
      return;
    }

    const route = getNotificationRoute(
      notification.type,
      notification.reference_type,
      notification.reference_id,
      notification.title,
      notification.url
    );
    navigate(route);
  };

  // 키보드 단축키
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 브라우저 알림 권한 요청
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const handleSwitchCompany = async (companyCode: string) => {
    console.log('[Header] 🔄 handleSwitchCompany called with:', companyCode);
    try {
      console.log('[Header] 📡 Calling authStore.switchCompany...');
      await switchCompany(companyCode);
      console.log('[Header] ✅ switchCompany complete, navigating to /');
      // 기업 전환 후 홈으로 이동
      navigate("/");
      // 페이지 새로고침하여 모든 상태 초기화
      console.log('[Header] 🔄 Reloading page...');
      window.location.reload();
    } catch (error) {
      console.error("[Header] ❌ Failed to switch company:", error);

      // Extract error code from axios error
      const axiosError = error as { response?: { data?: { code?: string; error?: string; message?: string; detail?: string } } };
      const errorCode = axiosError.response?.data?.code;
      const errorMessage = axiosError.response?.data?.error ||
                          axiosError.response?.data?.message ||
                          axiosError.response?.data?.detail ||
                          (error instanceof Error ? error.message : '알 수 없는 오류');

      // Handle different error codes
      if (errorCode === 'APPROVAL_PENDING') {
        // 승인 대기 중인 경우 - 알림 페이지로 이동 또는 메시지 표시
        alert('해당 기업의 승인이 대기 중입니다.\n관리자의 승인을 기다려주세요.');
        // 홈으로 이동 (customer 계정 유지)
        navigate('/');
      } else if (errorCode === 'APPROVAL_REJECTED') {
        // 승인 거부된 경우
        alert(`해당 기업의 가입이 거부되었습니다.\n${errorMessage}`);
        navigate('/');
      } else if (errorCode === 'ACCOUNT_DISABLED') {
        // 계정 비활성화된 경우
        alert('해당 기업의 계정이 비활성화되었습니다.\n관리자에게 문의해주세요.');
        navigate('/');
      } else if (errorCode === 'NO_ACCESS_TO_COMPANY') {
        // 해당 기업에 계정이 없는 경우
        alert('해당 기업에 등록된 계정이 없습니다.');
        navigate('/');
      } else {
        // 기타 오류
        alert(`기업 전환 실패: ${errorMessage}`);
      }
    }
  };

  const handleCloseBanner = () => {
    setShowEvaluationBanner(false);
  };

  const handleBannerClick = () => {
    navigate("/competency/my-tasks");
  };

  const formatNotificationTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "방금 전";
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString("ko-KR");
  };

  const displayNotifications = storeNotifications.length > 0
    ? storeNotifications
    : notificationData?.items || [];
  // 스토어가 mark_as_read / WS notification_count 로 항상 동기화되므로 스토어를 신뢰.
  // 서버 응답은 초기 마운트(스토어가 아직 0이고 useEffect로 채워지기 전) 폴백으로만 사용.
  const displayUnreadCount = storeNotifications.length > 0
    ? storeUnreadCount
    : (notificationData?.unread_count ?? 0);

  return (
    <>
      {/* Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="p-0 w-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-lg mx-auto mx-auto">
          <DialogTitle className="sr-only">통합 검색</DialogTitle>
          <Command className="rounded-lg border-0" shouldFilter={false}>
            <CommandInput
              placeholder="검색어를 입력하세요... (Ctrl+K)"
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isSearching && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">검색 중...</span>
                </div>
              )}
              {!isSearching && searchQuery && searchResults.length === 0 && (
                <CommandEmpty>"{searchQuery}"에 대한 검색 결과가 없습니다.</CommandEmpty>
              )}
              {!isSearching && searchResults.length > 0 && (
                <CommandGroup heading={`검색 결과 (${searchResults.length}개)`}>
                  {searchResults.map((result) => (
                    <CommandItem
                      key={`${result.type}-${result.id}`}
                      onSelect={() => handleSelectResult(result)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col gap-1 w-full">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{result.title}</span>
                          <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
                            {result.type}
                          </span>
                        </div>
                        {result.description && (
                          <span className="text-sm text-muted-foreground line-clamp-1">
                            {result.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {!searchQuery && recentSearches.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="최근 검색어">
                    {recentSearches.map((query) => (
                      <CommandItem
                        key={query}
                        onSelect={() => {
                          setSearchQuery(query);
                          handleSearch(query);
                        }}
                      >
                        <Search className="mr-2 h-4 w-4" />
                        {query}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

    <header className="flex h-16 items-center justify-between border-b bg-card px-4">
      {/* Left section */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden touch-target"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </Button>

        {/* Desktop sidebar toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="hidden lg:flex"
          onClick={toggleSidebar}
        >
          {sidebarCollapsed ? (
            <PanelLeft className="h-5 w-5" />
          ) : (
            <PanelLeftClose className="h-5 w-5" />
          )}
        </Button>

        {/* Search - Mobile & Desktop */}
        <Button
          variant="outline"
          className="relative md:w-64 w-10 h-10 md:h-9 justify-start text-muted-foreground touch-target hidden sm:flex"
          onClick={() => setSearchOpen(true)}
        >
          <Search className="h-5 w-5 md:h-4 md:w-4 md:mr-2" />
          <span className="hidden md:inline">검색...</span>
          <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 lg:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* Sticky Notes - employee + 위젯 권한 체크 */}
        {user && isEmployee(user.user_type) && myWidgets.includes("sticky-notes") && (
          <Button
            variant="ghost"
            size="icon"
            className="relative touch-target"
            onClick={() => navigate("/sticky-notes")}
            title="포스트잇"
          >
            <StickyNote className="h-5 w-5 md:h-5 md:w-5" />
            {stickyNoteUnreadData && stickyNoteUnreadData.count > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
                {stickyNoteUnreadData.count > 99 ? "99+" : stickyNoteUnreadData.count}
              </span>
            )}
          </Button>
        )}

        {/* Lottery - 미개봉 복권이 있을 때만 표시 */}
        {user && isEmployee(user.user_type) && hasMenuPath(userMenus, "/lottery") && lotteryPendingData && lotteryPendingData.count > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="relative touch-target"
            onClick={() => navigate("/lottery")}
            title="미개봉 복권"
          >
            <Ticket className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-xs font-semibold text-white">
              {lotteryPendingData.count > 99 ? "99+" : lotteryPendingData.count}
            </span>
          </Button>
        )}

        {/* Schedule - employee + 위젯 권한 체크 */}
        {user && isEmployee(user.user_type) && myWidgets.includes("calendar") && (
          <Button
            variant="ghost"
            size="icon"
            className="touch-target"
            onClick={() => navigate("/calendar")}
            title="일정"
          >
            <CalendarDays className="h-5 w-5 md:h-5 md:w-5" />
          </Button>
        )}

        {/* Organization Chart - employee + 위젯 권한 체크 + 활성 직원 존재 */}
        {hasOrgAccess && (orgChartData?.total_employees ?? 0) > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="touch-target"
            onClick={() => setOrgSidebarOpen(true)}
            title="조직도"
          >
            <Building2 className="h-5 w-5 md:h-5 md:w-5" />
          </Button>
        )}

        {/* Chat - employee + 위젯 권한 체크 */}
        {user && isEmployee(user.user_type) && myWidgets.includes("chat") && !hideChatIcon && (
          <Button
            variant="ghost"
            size="icon"
            className="relative touch-target"
            onClick={handleChatIconClick}
            title={mobileOutdated ? "메신저 앱 업데이트 필요 — 클릭해서 안내 보기" : routeChatToMobileApp ? "메신저 앱 설치 — 클릭해서 안내 보기" : desktopInstallNeeded ? "Windows 메신저 설치·실행 — 클릭해서 안내 보기" : routeChatToDesktopApp ? "메신저 앱에서 확인" : "메신저"}
          >
            <MessageSquare className="h-5 w-5 md:h-5 md:w-5" />
            {(desktopInstallNeeded || routeChatToMobileApp) && totalChatUnreadCount === 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background"
                aria-label="메신저 설치 필요"
              />
            )}
            {totalChatUnreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -right-1 -top-1 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center"
              >
                {totalChatUnreadCount > 99 ? "99+" : totalChatUnreadCount}
              </Badge>
            )}
          </Button>
        )}

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative touch-target">
              <Bell className="h-5 w-5 md:h-5 md:w-5" />
              {displayUnreadCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -right-1 -top-1 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center"
                >
                  {displayUnreadCount > 99 ? "99+" : displayUnreadCount}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] max-w-96">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0">알림</DropdownMenuLabel>
              {displayUnreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto py-1 px-2 text-xs"
                  onClick={(e) => {
                    e.preventDefault();
                    markAllAsReadMutation.mutate();
                  }}
                  disabled={markAllAsReadMutation.isPending}
                >
                  {markAllAsReadMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      모두 읽음
                    </>
                  )}
                </Button>
              )}
            </div>
            <DropdownMenuSeparator />
            <ScrollArea className="h-96">
              {isLoadingNotifications ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : displayNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Bell className="h-8 w-8 mb-2" />
                  <p className="text-sm">알림이 없습니다</p>
                </div>
              ) : (
                displayNotifications.map((notification) => {
                  const Icon = notificationIcons[notification.type] || Info;
                  return (
                    <DropdownMenuItem
                      key={notification.id}
                      className={`flex items-start gap-3 py-3 px-3 cursor-pointer ${
                        !notification.is_read ? "bg-muted/50" : ""
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className={`mt-0.5 p-1.5 rounded-full ${
                        notification.is_read ? "bg-muted" : "bg-primary/10"
                      }`}>
                        <Icon className={`h-4 w-4 ${
                          notification.is_read ? "text-muted-foreground" : "text-primary"
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${
                          !notification.is_read ? "font-medium" : ""
                        }`}>
                          {notification.title}
                        </p>
                        {notification.content && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {notification.content}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatNotificationTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      )}
                    </DropdownMenuItem>
                  );
                })
              )}
            </ScrollArea>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="justify-center text-primary">
              <Link to="/notifications">모든 알림 보기</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-2 touch-target">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.id ? getProfileImageUrl(user.id) : undefined} />
                <AvatarFallback>
                  {user?.name ? getInitials(user.name) : "U"}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium md:block">
                {user?.name}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] max-w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{user?.name}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>프로필</span>
              </Link>
            </DropdownMenuItem>

            {/* 기업 전환 메뉴 (2개 이상의 기업에 소속된 경우만 표시) */}
            {myCompaniesData && myCompaniesData.total > 1 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>기업 전환</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {myCompaniesData.companies.map((company) => (
                    <DropdownMenuItem
                      key={company.company_id}
                      onClick={() => !company.is_current && handleSwitchCompany(company.company_code)}
                      disabled={company.is_current}
                      className={company.is_current ? "bg-accent" : ""}
                    >
                      <div className="flex items-center gap-2 w-full">
                        {company.logo_url ? (
                          <img
                            src={company.logo_url}
                            alt={company.company_name}
                            className="h-5 max-w-[90px] object-contain"
                          />
                        ) : (
                          <>
                            <Building2 className="h-4 w-4 shrink-0" />
                            <span className="flex-1">{company.company_name}</span>
                          </>
                        )}
                        {company.is_current && (
                          <Check className="h-4 w-4 text-primary ml-auto" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}

            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                {theme === "light" && <Sun className="h-4 w-4" />}
                {theme === "dark" && <Moon className="h-4 w-4" />}
                {theme === "system" && <Monitor className="h-4 w-4" />}
                <span>테마</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>라이트</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>다크</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <Monitor className="mr-2 h-4 w-4" />
                  <span>시스템</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>로그아웃</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>

    {/* 역량 평가 알림 배너 */}
    {user && isEmployee(user.user_type) && showEvaluationBanner && competencyEvaluationCount > 0 && (
      <div
        className="bg-purple-50 dark:bg-purple-950 border-b border-purple-200 dark:border-purple-800 px-4 py-3 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors"
        onClick={handleBannerClick}
      >
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-full">
              <ClipboardCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex flex-col">
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                평가 업무 {competencyEvaluationCount}건이 설정되었습니다.
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300">
                클릭하여 평가를 완료해주세요.
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-800"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseBanner();
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )}

    {/* Chat Sidebar */}
    <ChatSidebar open={chatSidebarOpen} onOpenChange={setChatSidebarOpen} />

    {/* Organization Sidebar */}
    <OrganizationSidebar open={orgSidebarOpen} onOpenChange={setOrgSidebarOpen} />
    </>
  );
}
